import { useEffect } from "react";

// The home page's <title>: brand, then the phrases the site is built to rank
// for. public/index.html carries the same (the shell every page starts from).
export const DEFAULT_TITLE = "Solstack — Solana Token Tools: Create, Multisend, Airdrop & Inspect";
export const DEFAULT_DESCRIPTION =
  "Solana token tools: SPL and Token-2022 creator, multisender, airdrop, holder snapshot, token inspector and locks. Your keys stay in your wallet.";

// Bing Webmaster reports a <title> over 70 characters as an SEO issue
// ("Title too long") and Google cuts one off near there, so a long page
// title drops its section name ("· Solstack Blog" → "· Solstack"), then the
// brand. scripts/prerender.js fails the build on a title still over 70;
// give that page a shorter `seoTitle`.
export const TITLE_MAX = 70;
export const pageTitle = (title, siteName) =>
  [`${title} · ${siteName}`, `${title} · Solstack`].find((t) => [...t].length <= TITLE_MAX) || title;

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, name] = selector.match(/\[(.+?)=/) || [];
    if (name) el.setAttribute(name, selector.match(/"(.+?)"/)[1]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

/**
 * Sets a unique document title + meta description (+ OG/Twitter mirrors and
 * a canonical link) for the current route, restoring the site-wide default
 * on unmount. This is a client-side-only SPA with no server rendering, so
 * this is what stands in for per-page <title>/<meta> — every docs page and
 * the marketing home otherwise all shared one static title from index.html.
 */
export function useDocumentMeta({ title, description, path, siteName = "Solstack Docs" } = {}) {
  useEffect(() => {
    const fullTitle = title ? pageTitle(title, siteName) : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;

    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);

    if (path) {
      const url = `https://solstack.xyz${path}`;
      setMeta('meta[property="og:url"]', "content", url);
      setMeta('meta[name="twitter:url"]', "content", url);
      let canonical = document.head.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", url);
    }

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:title"]', "content", DEFAULT_TITLE);
      setMeta('meta[property="og:description"]', "content", DEFAULT_DESCRIPTION);
      setMeta('meta[name="twitter:title"]', "content", DEFAULT_TITLE);
      setMeta('meta[name="twitter:description"]', "content", DEFAULT_DESCRIPTION);
    };
  }, [title, description, path, siteName]);
}
